import React from 'react';
import PropTypes from 'prop-types';

import { strings } from '../../../../localization';

import Switch from '../../../../components/common/Switch';
import ReactSlider from 'react-slider';
import styled from 'styled-components'; // styling react-slider
import ProgressOverlay from '../../../../components/common/ProgressOverlay';

import { UPDATE_SETTINGS_TIMEOUT } from '../GlobalSettings';

const TAILGATING_DETECTION_MIN_VAL_REACTION_TIME = 0.5; // 1 second 
const TAILGATING_DETECTION_MAX_VAL_REACTION_TIME = 5; // 5 seconds
const TAILGATING_DETECTION_MIN_VAL_FOLLOW_TIME = 5; // minimum follow time
const TAILGATING_DETECTION_MAX_VAL_FOLLOW_TIME = 50; // maximum follow time before tailgating is triggered

const EVENTS_SPEED_THRESHOLD_CONSTANT_MIN_METRIC = 50;
const EVENTS_SPEED_THRESHOLD_CONSTANT_MAX_METRIC = 160;

const EVENTS_SPEED_THRESHOLD_CONSTANT_MIN_IMPERIAL = 31;
const EVENTS_SPEED_THRESHOLD_CONSTANT_MAX_IMPERIAL = 100;


export const TAILGATING_REACTION_TIME_THRESHOLD = {
    HALF_A_SECOND: 0.5,
    A_SECOND: 1,
    ONE_AND_A_HALF_SECONDS: 1.5,
    TWO_SECONDS: 2
}

export default class EventsTailgatingDetection extends React.PureComponent {

    static propTypes = {
        eventConfigAvailable: PropTypes.bool,
        eventAvailabilityPending: PropTypes.bool.isRequired,
        eventAvailabilityEnabled: PropTypes.bool,
        eventAvailabilityErrorMessage: PropTypes.string,
        applyEventAvailability: PropTypes.func.isRequired,
        timeoutEventAvailability: PropTypes.func.isRequired,

        eventSpeedThresholdPending: PropTypes.bool.isRequired,
        eventSpeedThreshold: PropTypes.number,
        eventSpeedThresholdErrorMessage: PropTypes.string,
        applyEventSpeedThreshold: PropTypes.func.isRequired,
        timeoutEventSpeedThreshold: PropTypes.func.isRequired,

        eventReactionTimeThresholdPending: PropTypes.bool.isRequired,
        eventReactionTimeThreshold: PropTypes.number,
        eventReactionTimeThresholdErrorMessage: PropTypes.string,
        applyEventReactionTimeThreshold: PropTypes.func.isRequired,
        timeoutEventReactionTimeThreshold: PropTypes.func.isRequired,

        eventFollowTimeThresholdPending: PropTypes.bool.isRequired,
        eventFollowTimeThreshold: PropTypes.number,
        eventFollowTimeThresholdErrorMessage: PropTypes.string,
        applyEventFollowTimeThreshold: PropTypes.func.isRequired,
        timeoutEventFollowTimeThreshold: PropTypes.func.isRequired,

        expandOrCollapseStateForUIReferences: PropTypes.func.isRequired,
        eventsTailgatingDetectionExpandReference: PropTypes.string.isRequired,
        eventsTailgatingDetectionExpandReferenceIsExpanded: PropTypes.bool.isRequired,
        unitOfMeasureDistanceIsMetric: PropTypes.bool
    }

    constructor(props) {

        super(props);

        this.state = {
            eventSpeedThreshold: undefined,
            eventSpeedThresholdValidationMessage: undefined,
            eventFollowTimeThreshold: undefined,
            eventFollowTimeThresholdValidationMessage: undefined,
            eventReactionTimeThreshold: undefined,
            eventReactionTimeThresholdValidationMessage: undefined,
        }

        this.applyEventAvailabilityTimeoutId = undefined;
        this.applyEventSpeedThresholdTimeoutId = undefined;
        this.applyEventFollowTimeThresholdTimeoutId = undefined;
        this.applyEventReactionTimeThresholdTimeoutId = undefined;
        this.unitOfMeasureDistanceIsMetric = true;
    }

    componentWillUnmount() {
        clearTimeout(this.applyEventAvailabilityTimeoutId);
        clearTimeout(this.applyEventThresholdTimeoutId);
    }

    /*componentDidUpdate(prevProps) {
    }*/

    discardEventSpeedThresholdChanges = () => {
        this.setState({
            eventSpeedThreshold: undefined,
            eventSpeedThresholdValidationMessage: undefined
        });
    }

    discardEventReactionTimeThresholdChanges = () => {
        this.setState({
            eventReactionTimeThreshold: undefined,
            eventReactionTimeThresholdValidationMessage: undefined
        });
    }

    discardEventFollowTimeThresholdChanges = () => {
        this.setState({
            eventFollowTimeThreshold: undefined,
            eventFollowTimeThresholdValidationMessage: undefined
        });
    }

    applyEventSpeedThresholdState = () => {

        clearTimeout(this.applyEventSpeedThresholdTimeoutId);
        
        // validate this attempt to update Speed threshold
        if (isNaN(this.state.eventSpeedThreshold)) { return; }

        let eventSpeedThreshold = this.state.eventSpeedThreshold;
        
        if (this.props.unitOfMeasureDistanceIsMetric !== true) {
            eventSpeedThreshold = eventSpeedThreshold * 1.609;
            if (eventSpeedThreshold < EVENTS_SPEED_THRESHOLD_CONSTANT_MIN_METRIC) {
                eventSpeedThreshold = EVENTS_SPEED_THRESHOLD_CONSTANT_MIN_METRIC;
            }
        }
    
        eventSpeedThreshold = Math.round(eventSpeedThreshold); // rounding added for RAV-3502

        this.props.applyEventSpeedThreshold(
            Number(eventSpeedThreshold),
            this.props.eventSpeedThreshold,
        )
        .then(
            () => {
                this.discardEventSpeedThresholdChanges();
                clearTimeout(this.applyEventSpeedThresholdTimeoutId);
            },
            () => {clearTimeout(this.applyEventSpeedThresholdTimeoutId)}
        )
    }

    applyEventReactionTimeThresholdState = () => {

        clearTimeout(this.applyEventsThresholdTimeoutId);

        // validate this attempt to update threshold
        if (isNaN(this.state.eventReactionTimeThreshold)) { return; }

        let eventReactionTimeThreshold = Number(this.state.eventReactionTimeThreshold);
        eventReactionTimeThreshold = Math.max(eventReactionTimeThreshold, TAILGATING_DETECTION_MIN_VAL_REACTION_TIME); // floor at 1 second
        eventReactionTimeThreshold = Math.min(eventReactionTimeThreshold, TAILGATING_DETECTION_MAX_VAL_REACTION_TIME); // ceiling at 5 seconds

        this.props.applyEventReactionTimeThreshold(
            Number(eventReactionTimeThreshold), // new
            this.props.eventReactionTimeThreshold // old (for undo)
        )
        .then(
            () => {
                this.discardEventReactionTimeThresholdChanges(); // clear state to hide apply/discard buttons
                clearTimeout(this.applyEventsThresholdTimeoutId);
            },
            () => {clearTimeout(this.applyEventsThresholdTimeoutId)}
        )
    }

    applyEventFollowTimeThresholdState = () => {

        clearTimeout(this.applyEventsThresholdTimeoutId);

        // validate this attempt to update threshold
        if (isNaN(this.state.eventFollowTimeThreshold)) { return; }

        let eventFollowTimeThreshold = Number(this.state.eventFollowTimeThreshold);
        eventFollowTimeThreshold = Math.max(eventFollowTimeThreshold, TAILGATING_DETECTION_MIN_VAL_FOLLOW_TIME); // floor at 2 seconds
        eventFollowTimeThreshold = Math.min(eventFollowTimeThreshold, TAILGATING_DETECTION_MAX_VAL_FOLLOW_TIME); // ceiling at 10 seconds

        this.props.applyEventFollowTimeThreshold(
            Number(eventFollowTimeThreshold), // new
            this.props.eventFollowTimeThreshold // old (for undo)
        )
        .then(
            () => {
                this.discardEventFollowTimeThresholdChanges(); // clear state to hide apply/discard buttons
                clearTimeout(this.applyEventsThresholdTimeoutId);
            },
            () => {clearTimeout(this.applyEventsThresholdTimeoutId)}
        )
    }

    handleSpeedThresholdChange = (event) => {

        const trimmedValue = event.target.value? event.target.value.trim() : null; // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)

        if (trimmedValue === null) {
            this.setState({
                eventSpeedThreshold: trimmedValue // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)
            });
            return;    
        }

        if (isNaN(trimmedValue)) { return; }

        const indexOfDecimal = trimmedValue.indexOf(".");
        if (indexOfDecimal !== -1) {
            if (indexOfDecimal < trimmedValue.length - 2) {// .01 || 0.01
                return; // .0 || 0.0
            }
        }

        let eventSpeedThresholdValidationMessage = undefined;
        if (this.props.unitOfMeasureDistanceIsMetric !== true) {
            if (Number(trimmedValue) < EVENTS_SPEED_THRESHOLD_CONSTANT_MIN_IMPERIAL || Number(trimmedValue) > EVENTS_SPEED_THRESHOLD_CONSTANT_MAX_IMPERIAL) { 
                eventSpeedThresholdValidationMessage = strings.formatString(strings.settings_global_events_threshold_needs_to_be_between_x_and_x_mp_h, EVENTS_SPEED_THRESHOLD_CONSTANT_MIN_IMPERIAL, EVENTS_SPEED_THRESHOLD_CONSTANT_MAX_IMPERIAL);
            }
        } else {
            if (Number(trimmedValue) < EVENTS_SPEED_THRESHOLD_CONSTANT_MIN_METRIC || Number(trimmedValue) > EVENTS_SPEED_THRESHOLD_CONSTANT_MAX_METRIC) { 
                eventSpeedThresholdValidationMessage = strings.formatString(strings.settings_global_events_threshold_needs_to_be_between_x_and_x_km_h, EVENTS_SPEED_THRESHOLD_CONSTANT_MIN_METRIC, EVENTS_SPEED_THRESHOLD_CONSTANT_MAX_METRIC);
            }
        }

        // check if need to clear the "apply" button when the value is changed back to the value in the cloud
        const sanitizedNumberRounded = Number(trimmedValue).toFixed(1); // rounded to whole number
        const eventSpeedThresholdRounded = Number(this.props.eventSpeedThreshold).toFixed(1);

        if (sanitizedNumberRounded === eventSpeedThresholdRounded) {
            if (indexOfDecimal === -1 ||
                indexOfDecimal < trimmedValue.length - 1) { // allow trailing decimal during input
                this.setState({
                    eventSpeedThresholdValidationMessage: undefined,
                    eventSpeedThreshold: undefined
                });
                return;
            }
        }

        this.setState({
            eventSpeedThreshold: trimmedValue, // allow trailing decimal during input
            eventSpeedThresholdValidationMessage: eventSpeedThresholdValidationMessage
        });
    }   

    handleFollowTimeThresholdChange = (event) => {
                
        let trimmedValue = event.target.value? event.target.value.trim() : null; // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)
        
        if (trimmedValue === null) {
            this.setState({
                eventFollowTimeThreshold: trimmedValue // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)
            });
            return;    
        }

        if (isNaN(trimmedValue)) { return; }

        let eventFollowTimeThresholdValidationMessage = undefined;
        if (Number(trimmedValue) < TAILGATING_DETECTION_MIN_VAL_FOLLOW_TIME) {
            eventFollowTimeThresholdValidationMessage = strings.formatString(strings.settings_global_events_threshold_needs_to_be_between_x_and_x_seconds, TAILGATING_DETECTION_MIN_VAL_FOLLOW_TIME, TAILGATING_DETECTION_MAX_VAL_FOLLOW_TIME);
        }

        if (Number(trimmedValue) > TAILGATING_DETECTION_MAX_VAL_FOLLOW_TIME) {
            eventFollowTimeThresholdValidationMessage = strings.formatString(strings.settings_global_events_threshold_needs_to_be_between_x_and_x_seconds, TAILGATING_DETECTION_MIN_VAL_FOLLOW_TIME, TAILGATING_DETECTION_MAX_VAL_FOLLOW_TIME);
        }

        // check if need to clear the "apply" button when the value is changed back to the value in the cloud
        const sanitizedNumberRounded = Number(trimmedValue).toFixed(1); // rounded to whole number
        const eventFollowTimeThresholdRounded = Number(this.props.eventFollowTimeThreshold).toFixed(1);

        if (sanitizedNumberRounded === eventFollowTimeThresholdRounded) {
            const indexOfDecimal = trimmedValue.indexOf(".");
            if (indexOfDecimal === -1 ||
                indexOfDecimal < trimmedValue.length - 1) { // allow trailing decimal during input
                this.setState({
                    eventFollowTimeThreshold: undefined,
                    eventFollowTimeThresholdValidationMessage: undefined
                });
                return;
            }
        }

        this.setState({
            eventFollowTimeThreshold: trimmedValue, 
            eventFollowTimeThresholdValidationMessage: eventFollowTimeThresholdValidationMessage
        });
    }

    handleReactionTimeThresholdChange = (event) => {

        let trimmedValue = event.target.value? event.target.value.trim() : null; // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)

        if (isNaN(trimmedValue)) { return; }

        let eventReactionTimeThresholdValidationMessage = undefined;
        if (Number(trimmedValue) < TAILGATING_DETECTION_MIN_VAL_REACTION_TIME) {
            eventReactionTimeThresholdValidationMessage = strings.formatString(strings.settings_global_events_threshold_needs_to_be_between_x_and_x_seconds, TAILGATING_DETECTION_MIN_VAL_REACTION_TIME, TAILGATING_DETECTION_MAX_VAL_REACTION_TIME);
        }

        if (Number(trimmedValue) > TAILGATING_DETECTION_MAX_VAL_REACTION_TIME) {
            eventReactionTimeThresholdValidationMessage = strings.formatString(strings.settings_global_events_threshold_needs_to_be_between_x_and_x_seconds, TAILGATING_DETECTION_MIN_VAL_REACTION_TIME, TAILGATING_DETECTION_MAX_VAL_REACTION_TIME);
        }

        // check if need to clear the "apply" button when the value is changed back to the value in the cloud
        const sanitizedNumberRounded = Number(trimmedValue).toFixed(1); // rounded to whole number
        const eventReactionTimeThresholdRounded = Number(this.props.eventReactionTimeThreshold).toFixed(1);

        if (sanitizedNumberRounded === eventReactionTimeThresholdRounded) {
            this.setState({
                eventReactionTimeThreshold: undefined, 
                eventReactionTimeThresholdValidationMessage: undefined
            });
            return;
        }

        this.setState({
            eventReactionTimeThreshold: trimmedValue, 
            eventReactionTimeThresholdValidationMessage: eventReactionTimeThresholdValidationMessage
        });
    }


    render() {

        if (!this.props.eventConfigAvailable) {
            return null;
        }

        // show apply button(s)?
        const showSpeedThresholdApplyButton = this.state.eventSpeedThreshold !== undefined ? true : false;
        const showReactionTimeThresholdApplyButton = this.state.eventReactionTimeThreshold !== undefined ? true : false;
        const showFollowTimeThresholdApplyButton = this.state.eventFollowTimeThreshold !== undefined ? true : false;
    
        const reactionTimeFormActionsClassName = showReactionTimeThresholdApplyButton ? "form-actions" : "form-actions collapsed";
        const followTimeFormActionsClassName = showFollowTimeThresholdApplyButton ? "form-actions" : "form-actions collapsed";
        const speedThresholdFormActionsClassName = showSpeedThresholdApplyButton ? "form-actions" : "form-actions collapsed";

        // determine which values to show in the input fields
        let eventReactionTimeThreshold = "";

        if (this.props.eventReactionTimeThreshold !== undefined) {
            eventReactionTimeThreshold = this.props.eventReactionTimeThreshold;
        }
        if (this.state.eventReactionTimeThreshold !== undefined) { // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)
            eventReactionTimeThreshold = this.state.eventReactionTimeThreshold ? this.state.eventReactionTimeThreshold : ""; // sanitizing truthy check to a blank string
        }

        let eventSpeedThreshold = "";
        if (this.props.eventSpeedThreshold !== undefined) {
            eventSpeedThreshold = this.props.eventSpeedThreshold;
        }
        if (this.state.eventSpeedThreshold !== undefined) { // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)
            eventSpeedThreshold = this.state.eventSpeedThreshold ? this.state.eventSpeedThreshold : "";
        }

        let eventFollowTimeThreshold = "";
        if (this.props.eventFollowTimeThreshold !== undefined) {
            eventFollowTimeThreshold = this.props.eventFollowTimeThreshold;
        }
        if (this.state.eventFollowTimeThreshold !== undefined) { // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)
            eventFollowTimeThreshold = this.state.eventFollowTimeThreshold ? this.state.eventFollowTimeThreshold : "";
        }
        
        // determine which unit of measure to show in the input label
        let speedThresholdConstantName = this.props.unitOfMeasureDistanceIsMetric !== true ? strings.settings_global_events_speeding_threshold_type_constant_imperial : strings.settings_global_events_speeding_threshold_type_constant_metric;

        return (
            <div className='setting-group-horiz' id="component_document_id_events_tailgating_detection">
                <div className={this.props.eventsTailgatingDetectionExpandReferenceIsExpanded ? "subgroup" : "subgroup collapsed"}>
                    <h5 className="subgroup-header">
                        <button disabled={true} // SUBSECTION_COLLAPSING_DISABLED
                            className={this.props.eventsTailgatingDetectionExpandReferenceIsExpanded ? "settings-disclosure-wrapper" : "settings-disclosure-wrapper collapsed"}
                            onClick={() => {
                                this.props.expandOrCollapseStateForUIReferences(
                                    !this.props.eventsTailgatingDetectionExpandReferenceIsExpanded,
                                    [this.props.eventsTailgatingDetectionExpandReference])
                            }}>
                            {strings.settings_global_events_tailgating_detection_header}
                            {this.props.showBetaDecoration && (
                                <span className="beta-decoration">
                                    {strings.settings_global_beta_feature_label}
                                    <span className="tooltiptext">
                                        {strings.settings_global_beta_feature_description}
                                        <a href="mailto:support@ravenconnected.com">
                                            {strings.settings_global_beta_feature_call_to_action}
                                        </a>
                                    </span>
                                </span>
                            )}
                            <i className="material-icons disclosure">keyboard_arrow_up</i>
                        </button>
                    </h5>
                    <h6 className="subgroup-subheader">
                        <div className='setting-group switch-group'>
                            <div>
                                <Switch
                                    onChange={() => {

                                        clearTimeout(this.applyEventAvailabilityTimeoutId);
                                        const eventAvailabilityEnabled = this.props.eventAvailabilityEnabled;
                                        this.applyEventAvailabilityTimeoutId = setTimeout(() => {this.props.timeoutEventAvailability(eventAvailabilityEnabled)}, UPDATE_SETTINGS_TIMEOUT);

                                        this.props.applyEventAvailability(!this.props.eventAvailabilityEnabled)
                                        .then(
                                            () => {clearTimeout(this.applyEventAvailabilityTimeoutId)},
                                            () => {clearTimeout(this.applyEventAvailabilityTimeoutId)}
                                        )
                                    }}
                                    checked={this.props.eventAvailabilityEnabled || false}
                                    disabled={this.props.eventAvailabilityPending}
                                    smallFormat={true}
                                />
                                <label disabled={this.props.eventAvailabilityPending}>{this.props.eventAvailabilityEnabled?
                                    strings.settings_global_events_tailgating_are_enabled
                                :
                                    strings.settings_global_events_tailgating_are_disabled
                                }</label>
                                {this.props.eventAvailabilityPending ? <ProgressOverlay /> : null }
                            </div>
                            <div className="error-message">
                                {this.props.eventAvailabilityErrorMessage}
                            </div>
                        </div>
                    </h6>

                    
                        <div className='setting-group threshold-slider'>
                            <div className="description">
                                {strings.settings_global_events_tailgating_min_speed_description}
                            </div>
                            <div className="form">
                                <div>
                                    <input type="text" autoComplete="off" className="threshold" value={eventSpeedThreshold} placeholder={strings.settings_global_events_speeding_threshold_placeholder} onChange={this.handleSpeedThresholdChange}></input>
                                </div>
                                <div>
                                    <label className="inline-label-large">
                                        {speedThresholdConstantName}
                                    </label>
                                </div>
                            </div>
                            <div className={speedThresholdFormActionsClassName}>
                                <button className="btn btn-blue" disabled={this.state.eventSpeedThresholdValidationMessage !== undefined} onClick={this.applyEventSpeedThresholdState}>{strings.action_apply}</button>
                                <button className="btn btn-outline-red" onClick={this.discardEventSpeedThresholdChanges}>{strings.settings_action_discard_changes}</button>
                            </div>
                            <div className="error-message form-indent">
                                {this.props.eventSpeedThresholdErrorMessage || this.state.eventSpeedThresholdValidationMessage}
                            </div>
                            {this.props.eventSpeedThresholdPending || this.props.unitOfMeasureDistancePending? <ProgressOverlay /> : null }
                        </div>
                    

                    
                        <div className='setting-group threshold-slider'>
                            <div className="description">
                                {strings.settings_global_events_tailgating_follow_time_description}
                            </div>
                            <div className="form">
                                <div>
                                    <input type="text" autoComplete="off" className="threshold" value={eventFollowTimeThreshold} placeholder={strings.settings_global_events_speeding_threshold_placeholder} onChange={this.handleFollowTimeThresholdChange}></input>
                                </div>
                                <div>
                                    <label className="inline-label-large">
                                        {strings.settings_global_events_time_type_seconds}
                                    </label>
                                </div>
                            </div>
                            <div className={followTimeFormActionsClassName}>  
                                <button className="btn btn-blue" disabled={this.state.eventFollowTimeThresholdValidationMessage !== undefined} onClick={this.applyEventFollowTimeThresholdState}>{strings.action_apply}</button>
                                <button className="btn btn-outline-red" onClick={this.discardEventFollowTimeThresholdChanges}>{strings.settings_action_discard_changes}</button>
                            </div>
                            <div className="error-message form-indent">
                                {this.state.eventFollowTimeThresholdValidationMessage}
                            </div>
                            {this.props.eventFollowTimeThresholdPending ? <ProgressOverlay /> : null }
                        </div>
                    

                    

                    
                        <div className='setting-group threshold-slider'>
                            <div className="description">
                                {strings.settings_global_events_tailgating_reaction_time_description}

                            </div>
                            <div className="form">
                                <div>
                                    <select name="reaction-time-threshold" value={eventReactionTimeThreshold} onChange={this.handleReactionTimeThresholdChange}>
                                        {Object.entries(TAILGATING_REACTION_TIME_THRESHOLD).map(([key, value]) => (
                                            <option key={key} value={value}>
                                                &lt; {value.toString()}
                                            </option>
                                        ))}                                        
                                        
                                    </select>
                                    
                                </div>
                                <div>
                                    <label className="inline-label-large">
                                        {strings.settings_global_events_time_type_seconds}
                                    </label>
                                </div>
                            </div>
                           

                            <div className={reactionTimeFormActionsClassName}>
                                <button className="btn btn-blue" disabled={this.state.eventReactionTimeThresholdValidationMessage !== undefined} onClick={this.applyEventReactionTimeThresholdState}>{strings.action_apply}</button>
                                <button className="btn btn-outline-red" onClick={this.discardEventReactionTimeThresholdChanges}>{strings.settings_action_discard_changes}</button>
                            </div>
                            
                            <div className="error-message form-indent">
                                {this.props.eventReactionTimeThresholdErrorMessage || this.state.eventReactionTimeThresholdValidationMessage}
                            </div>
                            {this.props.eventReactionTimeThresholdPending ? <ProgressOverlay /> : null }

                        </div>
                    
                    
                </div>
            </div>
        );
    }
}